import { useQuery } from '@tanstack/react-query';
import {
  MilestonePhaseItemDto,
  MilestoneTemplateWithPhasesDto,
} from 'src/api/FiretrolApiClient';
import {
  milestonePhaseItemApiClient,
  milestoneTemplateApiClient,
} from 'src/api/FiretrolApiClientInstances';
import { queryKeys } from 'src/api/queries/queryKeys';
import { toCancelToken } from 'src/api/queries/queryClient';

export interface MilestoneTemplateDetail {
  template: MilestoneTemplateWithPhasesDto['template'];
  phases: MilestoneTemplateWithPhasesDto['phases'];
  items: MilestonePhaseItemDto[];
}

/** The template's phases and its phase items share one cache entry so a
 * single invalidation refreshes the whole editor. */
export function useMilestoneTemplateDetail(templateId: string) {
  return useQuery<MilestoneTemplateDetail>({
    queryKey: queryKeys.milestoneTemplates.detail(templateId),
    queryFn: async ({ signal }) => {
      const cancelToken = toCancelToken(signal);
      const data = await milestoneTemplateApiClient.getTemplateById(
        templateId,
        cancelToken,
      );
      const items = await milestonePhaseItemApiClient.listPhaseItems(
        'default',
        { default: true, phase: null, milestoneTemplateId: templateId },
        cancelToken,
      );
      return { template: data.template, phases: data.phases, items };
    },
    enabled: templateId !== '',
    staleTime: 0,
    meta: { errorContext: 'milestone template' },
  });
}
